import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getTestimonials } from "../utils/testimonialFirestore";

const TestimonialSlider = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTestimonials();
      setTestimonials(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  useEffect(() => {
    if (isAutoPlaying && testimonials.length > 1) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
  }, [isAutoPlaying, nextSlide, testimonials.length]);

  if (loading) {
    return (
      <section className="bg-[#111111] relative py-32 overflow-hidden text-center text-white">
        <div className="animate-pulse">Loading testimonials...</div>
      </section>
    );
  }

  if (!testimonials.length) {
    return (
      <section className="bg-[#111111] relative py-32 overflow-hidden text-center text-white">
        <div>No testimonials available.</div>
      </section>
    );
  }

  return (
    <section className="bg-[#111111] relative py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgb(1,178,128)_1px,_transparent_0)] [background-size:40px_40px]" />
      </div>

      {/* Progress Bar */}
      <div className="absolute top-12 left-1/2 -translate-x-1/2">
        <div className="flex gap-2">
          {testimonials.map((_, index) => (
            <div
              key={index}
              className="w-16 h-[2px] bg-white/10 overflow-hidden"
            >
              <motion.div
                className="h-full bg-primary-green"
                initial={{ width: "0%" }}
                animate={{
                  width: currentIndex === index ? "100%" : "0%",
                }}
                transition={{
                  duration: currentIndex === index ? 5 : 0,
                  ease: "linear",
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="flex flex-col">
            <motion.span
              className="block text-primary-green mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              WHAT BRANDS ARE SAYING
            </motion.span>

            {/* Content Container */}
            <div className="mb-8 min-h-[300px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6 }}
                >
                  <blockquote className="text-white text-2xl leading-tight mb-8">
                    "{testimonials[currentIndex].quote}"
                  </blockquote>
                  <div>
                    <p className="text-white font-medium text-lg">
                      {testimonials[currentIndex].author}
                    </p>
                    <p className="text-white/60">
                      {testimonials[currentIndex].role}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation - Now in its own container after content */}
            <div className="relative z-10 flex items-center gap-4">
              <button
                onClick={prevSlide}
                className="p-2 border border-white/10 rounded-full hover:border-primary-green hover:text-primary-green transition-colors text-white"
                disabled={testimonials.length < 2}
              >
                <ArrowLeft size={24} />
              </button>
              <button
                onClick={nextSlide}
                className="p-2 border border-white/10 rounded-full hover:border-primary-green hover:text-primary-green transition-colors text-white"
                disabled={testimonials.length < 2}
              >
                <ArrowRight size={24} />
              </button>
            </div>
          </div>

          {/* Image */}
          <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0"
              >
                <img
                  src={testimonials[currentIndex].image}
                  alt={testimonials[currentIndex].author}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSlider;
